import type { Lang } from '../../stores/languageStore';
import { isLaunchedBridgeStatus, type LaunchedBridgeStatus } from './bridgeCause';
import { toAllowedBridgeUrl } from './bridgeUrlPolicy';

/**
 * The desktop launch `src` token is a URL-safe base64 encoding of a JSON object. It is unsigned, so
 * nothing decoded from it may be trusted without a check of its own.
 */

export interface DesktopLaunchTokenPayload {
    icIdentifiantPublique: string | null;
    licenseCIU?: string | null;
    /** Acomba's working language for the launching user; null when the desktop client predates it. */
    lang?: Lang | null;
    highWaterMark?: string | number | null;
    /**
     * The Acomba Usager's code (UsNumero), null when the desktop client predates it. Lets the silent
     * session-reuse fast path (see DesktopAuth.tsx's tryReuseDesktopSession) detect that a *different*
     * Usager opened the app under the same société/licence and fall through to the interactive login
     * instead of reusing the previous Usager's session.
     */
    username?: string | null;
    /** Where Acomba found the bridge listening; null when the desktop client predates it. */
    bridgeBaseUrl?: string | null;
    /** The secret the bridge demands on every call; null when the desktop client predates it. */
    bridgeNonce?: string | null;
    /** What Acomba found on the poste. Null when the desktop client predates it. */
    bridgeStatus?: LaunchedBridgeStatus | null;
}

function isLang(value: unknown): value is Lang {
    return value === 'fr' || value === 'en';
}

/** Decodes a URL-safe base64 token into its JSON payload, or null on any malformed input. */
export function decodeDesktopLaunchToken(token: string): DesktopLaunchTokenPayload | null {
    try {
        const standard = token.replace(/-/g, '+').replace(/_/g, '/');
        const padded = standard + '==='.slice(0, (4 - (standard.length % 4)) % 4);
        const json = atob(padded);
        const parsed: unknown = JSON.parse(json);
        if (typeof parsed !== 'object' || parsed === null) return null;
        const obj = parsed as Record<string, unknown>;
        return {
            icIdentifiantPublique: typeof obj.icIdentifiantPublique === 'string' ? obj.icIdentifiantPublique : null,
            licenseCIU: typeof obj.licenseCIU === 'string' ? obj.licenseCIU : null,
            lang: isLang(obj.lang) ? obj.lang : null,
            highWaterMark: (typeof obj.highWaterMark === 'string' || typeof obj.highWaterMark === 'number') ? obj.highWaterMark : null,
            username: typeof obj.username === 'string' ? obj.username : null,
            // Checked here rather than at the call site so a refused address never escapes the decoder.
            bridgeBaseUrl: toAllowedBridgeUrl(typeof obj.bridgeBaseUrl === 'string' ? obj.bridgeBaseUrl : null),
            bridgeNonce: typeof obj.bridgeNonce === 'string' && obj.bridgeNonce.length > 0 ? obj.bridgeNonce : null,
            bridgeStatus: isLaunchedBridgeStatus(obj.bridgeStatus) ? obj.bridgeStatus : null,
        };
    } catch {
        return null;
    }
}

export function extractLaunchedIcIdentifiantPublique(token: string | null | undefined): string | null {
    if (!token) return null;
    return decodeDesktopLaunchToken(token)?.icIdentifiantPublique ?? null;
}

/**
 * The launched copy's high-water mark, relayed on sync so the stale-copy check has a mark to compare
 * and advance. Same value the backend's desktop-auth resolves from the assertion.
 */
export function extractLaunchedHighWaterMark(token: string | null | undefined): number | null {
    if (!token) return null;
    const raw = decodeDesktopLaunchToken(token)?.highWaterMark ?? null;
    if (raw === null) return null;
    const value = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(value) ? value : null;
}

/**
 * The language Acomba is running in for the launching user. Without it the app falls back to the CEF
 * browser's locale, which follows Windows rather than the user's Acomba preference. Null when the
 * desktop client does not send the field, and callers then leave the detected language alone.
 */
export function extractLaunchedLang(token: string | null | undefined): Lang | null {
    if (!token) return null;
    return decodeDesktopLaunchToken(token)?.lang ?? null;
}

/**
 * Extracts the launching Acomba Usager's code from a launch token string. Returns null when the token
 * is absent, malformed, or produced by a desktop client that does not send the field.
 */
export function extractLaunchedUsername(token: string | null | undefined): string | null {
    if (!token) return null;
    return decodeDesktopLaunchToken(token)?.username ?? null;
}

/**
 * The bridge address Acomba resolved for this launch, already checked against
 * {@link toAllowedBridgeUrl} — so null also covers an address the client refuses to call.
 */
export function extractLaunchedBridgeBaseUrl(token: string | null | undefined): string | null {
    if (!token) return null;
    return decodeDesktopLaunchToken(token)?.bridgeBaseUrl ?? null;
}

/** The secret this launch's bridge will demand; null when Acomba minted none. */
export function extractLaunchedBridgeNonce(token: string | null | undefined): string | null {
    if (!token) return null;
    return decodeDesktopLaunchToken(token)?.bridgeNonce ?? null;
}

/**
 * What Acomba found on the poste before it opened this window. The browser cannot tell a bridge that
 * was never installed from one that is stopped, or from one whose port something else is holding;
 * Acomba read the files and called /health, so it can.
 */
export function extractLaunchedBridgeStatus(token: string | null | undefined): LaunchedBridgeStatus | null {
    if (!token) return null;
    return decodeDesktopLaunchToken(token)?.bridgeStatus ?? null;
}
